import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supportAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  HelpCircle, 
  MessageSquare,
  ChevronDown,
  Clock,
  CheckCircle,
  Send
} from 'lucide-react';

// FAQ Data
const faqs = [
  {
    question: 'How do I link my MeTag card?',
    answer: 'After purchasing, scan the QR code on your card or tap it on your phone. You\'ll be redirected to your dashboard where you can enter your profile URL.'
  },
  {
    question: 'Can I change the linked URL later?',
    answer: 'Yes! You can update your linked URL anytime from the My Cards section in your dashboard.'
  },
  {
    question: 'What happens when someone scans my card?',
    answer: 'They\'ll be instantly redirected to the URL you\'ve linked - whether that\'s your LinkedIn, portfolio, Meelink bio page, or any other website.'
  },
  {
    question: 'How long does shipping take?',
    answer: 'We ship within 1-3 business days. Delivery typically takes 3-7 days depending on your location.'
  },
  {
    question: 'Is there a refund policy?',
    answer: 'Due to the personalized nature of our products, we don\'t offer returns. However, if your card is defective, please contact support.'
  }
];

// FAQ Accordion Item
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="glass rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between text-left"
      >
        <span className="font-medium text-white">{question}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-4 pb-4">
          <p className="text-slate-400">{answer}</p>
        </div>
      )}
    </div>
  );
};

export default function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await supportAPI.getTickets();
      setTickets(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await supportAPI.createTicket(formData);
      toast.success('Support ticket created successfully!');
      setFormData({ subject: '', message: '' });
      fetchTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="support-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Support</h1>
        <p className="text-slate-400">Get help and find answers to common questions</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left Column - FAQs */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-violet-400" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <FAQItem key={index} {...faq} />
            ))}
          </div>
        </div>

        {/* Right Column - Create Ticket */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-violet-400" />
            Create Support Ticket
          </h2>

          <form onSubmit={handleSubmit} className="glass rounded-2xl p-6 mb-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Subject</label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  className="input-field"
                  placeholder="Brief description of your issue"
                  data-testid="ticket-subject"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={4}
                  className="input-field resize-none w-full"
                  placeholder="Describe your issue in detail..."
                  data-testid="ticket-message"
                />
              </div>
              <Button 
                type="submit"
                className="w-full btn-primary flex items-center justify-center gap-2"
                disabled={submitting}
                data-testid="ticket-submit-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Ticket'}
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>

          {/* Previous Tickets */}
          <h3 className="text-lg font-semibold text-white mb-4">Your Tickets</h3>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="glass rounded-xl p-6 text-center">
              <MessageSquare className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400">No tickets yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <motion.div
                  key={ticket.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass rounded-xl p-4"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-medium text-white">{ticket.subject}</h4>
                    <span className={`status-badge ${ticket.status === 'open' ? 'status-pending' : 'status-completed'}`}>
                      {ticket.status === 'open' ? (
                        <><Clock className="w-3 h-3 mr-1" /> Open</>
                      ) : (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Resolved</>
                      )}
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm line-clamp-2">{ticket.message}</p>
                  <p className="text-slate-500 text-xs mt-2">{formatDate(ticket.createdAt)}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Contact Info */}
      <div className="mt-8 glass rounded-2xl p-6 text-center">
        <p className="text-slate-400 mb-2">Need immediate help?</p>
        <p className="text-white">
          Call us at <a href="tel:+917837640500" className="text-violet-400">+91 78376 40500</a> or email{' '}
          <a href="mailto:support@metag.com" className="text-violet-400">support@metag.com</a>
        </p>
      </div>
    </div>
  );
}
